import { useEffect, useLayoutEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { t } from '@lingui/macro';
import { useVisible } from '@landr/maestro';
import { useOnboardingContext } from 'contexts/OnboardingContext/OnboardingContext';
import { isAPositionTarget } from 'contexts/OnboardingContext/OnboardingContext.utils';
import { Step } from 'contexts/OnboardingContext/OnboardingContext.type';
import { Timeout } from 'types/enums';
import { useGetTarget } from './OnboardingCoachmark.utils';

export const useOnboardingCoachmark = (): {
    Anchor: JSX.Element | undefined;
    targetElement: Element | undefined;
    step: Step | undefined;
    visible: boolean;
    isPathnameMatch: boolean;
    handleNext: () => void;
    handleExit: () => void;
    handleOnClickTarget: () => void;
    defaultNextCta: string;
    progression: string;
} => {
    // Context
    const { state, next, exit } = useOnboardingContext();
    const { step, current, total } = state;

    const target = step?.target;
    const focusSelector = step?.focus;
    const listenToTarget = step?.listenToTarget;
    const scrollIntoView =
        typeof step?.scrollIntoView !== 'undefined'
            ? step?.scrollIntoView
            : true;

    useLayoutEffect(() => {
        if (focusSelector) {
            const elementToFocus: HTMLElement = document.querySelector(
                focusSelector,
            ) as HTMLElement;

            elementToFocus?.focus();
        }
    }, [focusSelector]);

    const { currentTarget, targetElement, Anchor } = useGetTarget(target);

    // Navigation
    const location = useLocation();
    const isPathnameMatch =
        step?.pathname === undefined || step?.pathname === location.pathname;

    // Visibility
    const { visible, show, hide } = useVisible(true);

    // Default Next CTA
    const isLastStep = current + 1 === total;
    const progression =
        total > 1 && !isLastStep ? `(${current + 2}/${total})` : '';
    const defaultNextCta = isLastStep ? t`Got it!` : t`Next`;

    const handleNext = useCallback((): void => {
        hide();

        const nextStep = state.current + 1;

        // pass the next step number, to prevent skipping multiple steps when click fast
        setTimeout(() => {
            next(nextStep, show);
        }, Timeout.base);
    }, [hide, next, show, state]);

    const handleExit = (): void => {
        hide();
        exit();
    };

    const handleOnClickTarget = useCallback((): void => {
        listenToTarget && handleNext();
    }, [listenToTarget, handleNext]);

    // listen to click on target
    useEffect(() => {
        targetElement?.addEventListener('click', handleOnClickTarget);

        return (): void => {
            targetElement?.removeEventListener('click', handleOnClickTarget);
        };
    }, [targetElement, handleOnClickTarget]);

    // Scroll into view, on first load only
    useLayoutEffect(() => {
        const targetIsValid =
            isPathnameMatch &&
            !!currentTarget &&
            scrollIntoView &&
            !isAPositionTarget(currentTarget);
        const bufferHeight = 200;

        if (targetElement && targetIsValid) {
            const rect = targetElement.getBoundingClientRect();

            const scrollDownAllowed =
                rect.bottom > window.innerHeight - bufferHeight;
            const scrollUpAllowed = rect.top < bufferHeight;
            const scrollAllowed = scrollDownAllowed || scrollUpAllowed;

            if (scrollAllowed) {
                const scrollDownTo = rect.bottom - window.innerHeight / 2;
                const scrollUpTo =
                    rect.top - window.innerHeight / 2 + window.scrollY;

                window.scrollTo({
                    top: scrollDownAllowed ? scrollDownTo : scrollUpTo,
                    behavior: 'smooth',
                });
            }
        }
    }, [currentTarget, targetElement, isPathnameMatch, scrollIntoView]);

    return {
        Anchor,
        targetElement,
        step,
        visible,
        handleNext,
        handleExit,
        handleOnClickTarget,
        isPathnameMatch,
        defaultNextCta,
        progression,
    };
};
